/**
 * The date ranges a report is asked for.
 *
 * Presets first, because "this term" is what an owner actually wants and
 * working out that it means 1 September to 31 January is not their job. A term
 * here is the European school half-year — August to January and February to
 * July — which is what a German or British institution means by it. An American
 * one means something else, and the custom range is why that is survivable.
 */

export type RangePreset = "month" | "term" | "year" | "custom";

export interface DateRange {
  from: string;
  to: string;
}

/**
 * The backend takes calendar days, not instants.
 *
 * `YYYY-MM-DD` on both ends, inclusive: a report "to the 20th" includes the
 * lesson taught on the 20th, which is what anybody asking for it means.
 */
function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function startOfMonth(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}

/** The half-year containing `now`: February–July, or August–January. */
export function termBounds(now: Date): DateRange {
  const month = now.getUTCMonth();
  const year = now.getUTCFullYear();

  if (month >= 1 && month <= 6) {
    return {
      from: iso(new Date(Date.UTC(year, 1, 1))),
      to: iso(new Date(Date.UTC(year, 7, 0, 23, 59, 59, 999))),
    };
  }

  // August to January, which straddles the new year in one direction or the
  // other depending on which side of it we are standing.
  const startYear = month >= 7 ? year : year - 1;
  return {
    from: iso(new Date(Date.UTC(startYear, 7, 1))),
    to: iso(new Date(Date.UTC(startYear + 1, 1, 0, 23, 59, 59, 999))),
  };
}

export function rangeFor(preset: RangePreset, now: Date, custom?: DateRange): DateRange {
  switch (preset) {
    case "month":
      return { from: iso(startOfMonth(now)), to: iso(now) };
    case "term":
      return termBounds(now);
    case "year":
      return {
        from: iso(new Date(Date.UTC(now.getUTCFullYear(), 0, 1))),
        to: iso(now),
      };
    case "custom":
      return custom ?? { from: iso(startOfMonth(now)), to: iso(now) };
  }
}

/** A date input already gives `YYYY-MM-DD`, which is what the backend wants. */
export function fromDateInput(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
}

export function toDateInput(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms).toISOString().slice(0, 10) : "";
}
